import React, { useState, useEffect } from 'react';
import { ArrowLeft, Camera, X, TrendingUp, TrendingDown, RefreshCw, Coins } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatAmount } from '../utils/format';

const AddTransaction = () => {
  const {
    accounts,
    contacts,
    selectedTransaction,
    loading,
    error,
    setScreen,
    setError,
    addTransaction,
    updateTransaction,
    initialFormData,
    setInitialFormData
  } = useApp();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    accountId: '',
    toAccountId: '',
    amount: '',
    type: 'expense',
    description: '',
    tags: '',
    status: 'cleared',
    photo: null,
    loanDirection: 'borrowed',
    loanContact: ''
  });

  useEffect(() => {
    // Check if we have initial form data from context (e.g., from loan detail)
    if (initialFormData) {
      setFormData(prev => ({ ...prev, ...initialFormData }));
      setInitialFormData(null); // Clear after using
      return;
    }

    // Check if we're editing an existing transaction
    if (selectedTransaction && window.location.hash === '#edit') {
      setIsEditing(true);
      let loanDirection = 'borrowed';
      let loanContact = '';
      if (selectedTransaction.type === 'loan') {
        if (selectedTransaction.loanedBy) {
          loanDirection = 'borrowed';
          loanContact = selectedTransaction.loanedBy;
        } else if (selectedTransaction.borrowedBy) {
          loanDirection = 'lent';
          loanContact = selectedTransaction.borrowedBy;
        }
      }

      setFormData({
        accountId: selectedTransaction.accountId || selectedTransaction.fromAccount?.id || '',
        toAccountId: selectedTransaction.toAccount?.id || '',
        amount: Math.abs(parseFloat(selectedTransaction.amount)).toString(),
        type: selectedTransaction.type || 'expense',
        description: selectedTransaction.description || '',
        tags: selectedTransaction.tags || '',
        status: selectedTransaction.status || 'cleared',
        photo: null,
        loanDirection,
        loanContact
      });
    }
  }, [selectedTransaction, initialFormData]);

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.amount) {
      setError('Please enter an amount');
      return;
    }

    if (formData.type !== 'loan' && !formData.accountId) {
      setError('Please select an account');
      return;
    }

    if (formData.type === 'transfer' && !formData.toAccountId) {
      setError('Please select a destination account for transfer');
      return;
    }

    if (formData.type === 'loan' && !formData.loanContact) {
      setError('Please specify the contact for this loan');
      return;
    }

    try {
      if (isEditing) {
        await updateTransaction(selectedTransaction.id, formData);
      } else {
        await addTransaction(formData);
      }
      setScreen('confirmation');
    } catch (err) {
      // Error handled in context
    }
  };

  const handleBack = () => {
    setIsEditing(false);
    window.location.hash = '';
    setScreen('dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white shadow-sm p-4 flex items-center">
        <button onClick={handleBack} className="mr-4">
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-800">
          {isEditing ? 'Edit Transaction' : 'Add Transaction'}
        </h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Amount Input */}
        <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
          <label className="block text-sm font-medium text-gray-500 mb-3">Amount</label>
          <div className="flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-400 mr-2">$</span>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="text-5xl font-bold text-gray-800 text-center border-none focus:outline-none focus:ring-0 w-full"
              style={{ appearance: 'none' }}
            />
          </div>
        </div>

        {/* Transaction Type Selector */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <label className="block text-sm font-medium text-gray-700 mb-3">Transaction Type</label>
          <div className="grid grid-cols-4 gap-2">
            <button
              onClick={() => setFormData({ ...formData, type: 'income' })}
              className={`py-4 rounded-xl font-semibold transition ${
                formData.type === 'income'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <TrendingUp className="w-6 h-6 mx-auto mb-1" />
              Income
            </button>
            <button
              onClick={() => setFormData({ ...formData, type: 'expense' })}
              className={`py-4 rounded-xl font-semibold transition ${
                formData.type === 'expense'
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <TrendingDown className="w-6 h-6 mx-auto mb-1" />
              Expense
            </button>
            <button
              onClick={() => setFormData({ ...formData, type: 'transfer' })}
              className={`py-4 rounded-xl font-semibold transition ${
                formData.type === 'transfer'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ArrowLeft className="w-6 h-6 mx-auto mb-1 transform rotate-180" />
              Transfer
            </button>
            <button
              onClick={() => setFormData({ ...formData, type: 'loan' })}
              className={`py-4 rounded-xl font-semibold transition ${
                formData.type === 'loan'
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Coins className="w-6 h-6 mx-auto mb-1" />
              Loan
            </button>
          </div>
        </div>

        {/* Loan Fields */}
        {formData.type === 'loan' && (
          <div className="bg-white rounded-2xl p-6 shadow-lg space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Loan Direction</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, loanDirection: 'borrowed' })}
                  className={`py-3 rounded-xl font-medium transition ${
                    formData.loanDirection === 'borrowed'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  I Borrowed
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, loanDirection: 'lent' })}
                  className={`py-3 rounded-xl font-medium transition ${
                    formData.loanDirection === 'lent'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  I Lent
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {formData.loanDirection === 'borrowed'
                  ? 'You owe money to this person'
                  : 'This person owes you money'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.loanDirection === 'borrowed' ? 'Borrowed From' : 'Lent To'}
              </label>
              <input
                type="text"
                list="contacts-list"
                placeholder="Search contacts or enter email..."
                value={formData.loanContact}
                onChange={(e) => setFormData({ ...formData, loanContact: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              <datalist id="contacts-list">
                {contacts.map((contact, idx) => (
                  <option key={contact.id || contact.email || idx} value={contact.name || contact.email}>
                    {contact.email && contact.name ? `${contact.name} (${contact.email})` : ''}
                  </option>
                ))}
              </datalist>
              <p className="text-xs text-gray-500 mt-1">
                Select from list or type to add new contact
              </p>
            </div>
          </div>
        )}

        {/* Account Selection */}
        <div className="bg-white rounded-2xl p-6 shadow-lg space-y-4">
          {formData.type === 'transfer' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Account</label>
                <select
                  value={formData.accountId}
                  onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Account</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} (${formatAmount(parseFloat(account.balance))})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Account</label>
                <select
                  value={formData.toAccountId}
                  onChange={(e) => setFormData({ ...formData, toAccountId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Account</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} (${formatAmount(parseFloat(account.balance))})
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account</label>
              <select
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select Account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} (${formatAmount(parseFloat(account.balance))})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            placeholder="What's this transaction for?"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Tags */}
        {formData.type !== 'loan' && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
            <input
              type="text"
              placeholder="e.g., groceries, food, monthly"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
          </div>
        )}

        {/* Status */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setFormData({ ...formData, status: 'cleared' })}
              className={`py-3 rounded-xl font-semibold transition ${
                formData.status === 'cleared'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cleared
            </button>
            <button
              onClick={() => setFormData({ ...formData, status: 'pending' })}
              className={`py-3 rounded-xl font-semibold transition ${
                formData.status === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
          </div>
        </div>

        {/* Photo (Optional) */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <label className="block text-sm font-medium text-gray-700 mb-3">Receipt Photo (optional)</label>
          {formData.photo ? (
            <div className="relative">
              <img src={formData.photo} alt="Receipt" className="w-full rounded-xl" />
              <button
                onClick={() => setFormData({ ...formData, photo: null })}
                className="absolute top-3 right-3 bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-700 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              <Camera className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">Tap to take a photo</p>
              <p className="text-xs text-gray-400 mt-1">Optional receipt image</p>
            </label>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-indigo-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              {isEditing ? 'Updating...' : 'Adding...'}
            </div>
          ) : (
            isEditing ? 'Update Transaction' : 'Add Transaction'
          )}
        </button>
      </div>
    </div>
  );
};

export default AddTransaction;
